import {CACHE_TTL_MS} from '../config';

/** Bump the version whenever the shape of a cached value changes. */
const PREFIX = 'vch:v4:';

interface Entry<T> {
    value: T;
    savedAt: number;
}

export interface CachedValue<T> {
    value: T;
    fresh: boolean;
}

export function readCache<T>(key: string): CachedValue<T> | undefined {
    try {
        const raw = sessionStorage.getItem(PREFIX + key);
        if (!raw) return undefined;
        const entry = JSON.parse(raw) as Entry<T>;
        return {value: entry.value, fresh: Date.now() - entry.savedAt < CACHE_TTL_MS};
    } catch {
        return undefined; // storage disabled or corrupt entry
    }
}

function writeCache<T>(key: string, value: T) {
    try {
        sessionStorage.setItem(PREFIX + key, JSON.stringify({value, savedAt: Date.now()}));
    } catch {
        // quota exceeded or storage disabled – the site works without the cache
    }
}

const pending = new Map<string, Promise<unknown>>();

/** Runs `load` and caches its result. Concurrent calls for the same key share one request. */
export function loadAndCache<T>(key: string, load: () => Promise<T>): Promise<T> {
    let request = pending.get(key) as Promise<T> | undefined;
    if (!request) {
        request = load()
            .then(value => {
                writeCache(key, value);
                return value;
            })
            .finally(() => pending.delete(key));
        pending.set(key, request);
    }
    return request;
}
